import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AppState } from 'src/app/app.state';
import { Store } from '@ngxs/store';
import { ResetPageLayout, SavePageLayout } from 'src/app/app.actions';
import { DEFAULT_PAGE_LAYOUT, PageLayout, PageLayoutContent } from 'src/app/app.model';
import { Subject } from 'rxjs';
import { concatMap, debounceTime, filter, first, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-appearance-form',
    templateUrl: './appearance.component.html',
    exportAs: 'abpChangePasswordForm',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppearanceComponent implements OnInit, OnDestroy {
  pageLayoutContent: PageLayoutContent | null;
  form: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const pageLayoutContent$ = this.store.select(AppState.getPageLayoutContent).pipe(
      filter(s => s !== undefined),
      takeUntil(this.destroy$),
    );

    // Build the form once from the user's saved layout. Later store updates come from our own
    // saves, so they must not rebuild the form under the user.
    pageLayoutContent$.pipe(first()).subscribe(s => {
      this.buildForm((s as PageLayoutContent)?.pageLayout);
      this.cdr.detectChanges();
    });
    pageLayoutContent$.subscribe(s => (this.pageLayoutContent = s as PageLayoutContent));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(pageLayout?: PageLayout) {
    const layout = { ...DEFAULT_PAGE_LAYOUT, ...(pageLayout ?? {}) };
    this.form = this.fb.group({
      tableView: [String(layout.tableView)],
      noAnimations: [layout.noAnimations],
      showRowNo: [layout.showRowNo],
      recordsPerPage: [layout.recordsPerPage],
    });

    // Debounce collapses quick successive changes; concatMap queues saves so none is dropped
    // and they reach the server in order (each save sends the full form).
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        concatMap(() => this.save()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private async save() {
    try {
      await this.store.dispatch(new SavePageLayout(this.form.getRawValue() as PageLayout)).toPromise();
    } catch (error) {
      console.error('Error saving page layout:', error);
    }
  }

  async resetToDefaults() {
    try {
      await this.store.dispatch(new ResetPageLayout()).toPromise();
      this.form.setValue({ ...DEFAULT_PAGE_LAYOUT }, { emitEvent: false });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error resetting page layout:', error);
    }
  }
}
